import React, { useState } from 'react';
import { ClockIcon, SaveIcon, BeakerIcon, SettingsIcon, PlusCircleIcon, TrashIcon, ChevronUpIcon, ChevronDownIcon, GaugeIcon, XIcon, DatabaseIcon } from 'lucide-react';
import { useTimerSettings } from '../context/TimerContext';
interface Field {
  id: number;
  question: string;
  type: 'text' | 'number' | 'boolean';
}
interface MeasurementConfig {
  id: number;
  title: string;
  fields: Field[];
  required: boolean;
}
interface SampleConfig {
  id: number;
  title: string;
  timestamps: string[];
  notification: boolean;
  measurements: MeasurementConfig[];
  timingType?: 'custom' | 'start' | 'end' | 'both'; // Neue Eigenschaft für den Zeitpunkt
}
type SettingsTab = 'general' | 'samples' | 'machines' | 'tanks';
export function Settings() {
  const {
    interval,
    updateInterval
  } = useTimerSettings();
  const [minutes, setMinutes] = useState(interval / 60);
  const [activeTab, setActiveTab] = useState<SettingsTab>('general');
  const [samples, setSamples] = useState<SampleConfig[]>([{
    id: 1,
    title: 'QS Probe',
    timestamps: [],
    notification: false,
    measurements: [{
      id: 1,
      title: 'QS Messung',
      fields: [{
        id: 1,
        question: 'Haben die Güsse flecken?',
        type: 'boolean'
      }, {
        id: 2,
        question: 'Wurde das Rostpaket in der Hammermühle gewechselt?',
        type: 'boolean'
      }],
      required: true
    }],
    timingType: 'end'
  }, {
    id: 2,
    title: 'Test 1',
    timestamps: ['08:00'],
    notification: true,
    measurements: [{
      id: 1,
      title: 'QS Probe 3654',
      fields: [{
        id: 1,
        question: 'Passt das Granulat?',
        type: 'text'
      }, {
        id: 2,
        question: 'Entnehme 1 kg Feststoff',
        type: 'number'
      }],
      required: true
    }],
    timingType: 'custom'
  }, {
    id: 3,
    title: 'Qualitätskontrolle',
    timestamps: ['10:30', '14:30'],
    notification: true,
    measurements: [{
      id: 1,
      title: 'Standardmessung',
      fields: [{
        id: 1,
        question: 'pH-Wert',
        type: 'number'
      }, {
        id: 2,
        question: 'Viskosität',
        type: 'number'
      }, {
        id: 3,
        question: 'Bemerkungen zur Konsistenz',
        type: 'text'
      }],
      required: true
    }],
    timingType: 'custom'
  }]);
  const [expandedSample, setExpandedSample] = useState<number | null>(1); // Default to first sample expanded
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMachineType, setSelectedMachineType] = useState<string>('');
  const [newMachine, setNewMachine] = useState({
    name: '',
    type: '',
    settings: {
      tempLowerLimit: 45,
      tempUpperLimit: 60,
      pearlWearLimit: 10
    }
  });
  const [machines, setMachines] = useState<Array<{
    id: string;
    name: string;
    type: string;
    settings: {
      tempLowerLimit: number;
      tempUpperLimit: number;
      pearlWearLimit: number;
    };
    notifications: boolean;
  }>>([{
    id: '1',
    name: 'Perlmühle 1 (KD190/Ba)',
    type: 'KD190Ba',
    settings: {
      tempLowerLimit: 45,
      tempUpperLimit: 60,
      pearlWearLimit: 10
    },
    notifications: true
  }]);
  const availableMachineTypes = [{
    id: 'KD190Ba',
    name: 'Perlmühle KD190/Ba',
    description: 'Standard Perlmühle mit 630kg Perlenkapazität',
    capacity: 630
  }, {
    id: 'KD190Ba2',
    name: 'Perlmühle KD190/Ba2',
    description: 'Kompakte Perlmühle mit 575kg Perlenkapazität',
    capacity: 575
  }, {
    id: 'KD190Ba3',
    name: 'Perlmühle KD190/Ba3',
    description: 'Kompakte Perlmühle mit 575kg Perlenkapazität',
    capacity: 575
  }, {
    id: 'BuhlerT4',
    name: 'Bühler T4',
    description: 'Hochleistungs-Perlmühle mit 1150kg Perlenkapazität',
    capacity: 1150
  }];
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateInterval(minutes * 60);
  };
  const handleAddSample = () => {
    const newId = samples.length > 0 ? Math.max(...samples.map(s => s.id)) + 1 : 1;
    const newSample: SampleConfig = {
      id: newId,
      title: `Probe ${newId}`,
      timestamps: ['08:00'],
      notification: true,
      measurements: [],
      timingType: 'custom' // Standard ist benutzerdefinierte Zeit
    };
    setSamples(prevSamples => [...prevSamples, newSample]);
    // Automatically expand the newly created sample
    setExpandedSample(newId);
  };
  const handleAddMeasurement = (sampleId: number) => {
    setSamples(samples.map(sample => {
      if (sample.id === sampleId) {
        const newMeasurementId = Math.max(...sample.measurements.map(m => m.id), 0) + 1;
        return {
          ...sample,
          measurements: [...sample.measurements, {
            id: newMeasurementId,
            title: `Messung ${newMeasurementId}`,
            fields: [],
            required: false
          }]
        };
      }
      return sample;
    }));
  };
  const handleAddTimestamp = (sampleId: number) => {
    setSamples(samples.map(sample => {
      if (sample.id === sampleId) {
        return {
          ...sample,
          timestamps: [...sample.timestamps, '08:00']
        };
      }
      return sample;
    }));
  };
  const handleUpdateTimestamp = (sampleId: number, index: number, value: string) => {
    setSamples(samples.map(sample => {
      if (sample.id === sampleId) {
        const newTimestamps = [...sample.timestamps];
        newTimestamps[index] = value;
        return {
          ...sample,
          timestamps: newTimestamps
        };
      }
      return sample;
    }));
  };
  const handleDeleteTimestamp = (sampleId: number, index: number) => {
    setSamples(samples.map(sample => {
      if (sample.id === sampleId) {
        return {
          ...sample,
          timestamps: sample.timestamps.filter((_, i) => i !== index)
        };
      }
      return sample;
    }));
  };
  const handleAddField = (sampleId: number, measurementId: number) => {
    setSamples(samples.map(sample => {
      if (sample.id === sampleId) {
        return {
          ...sample,
          measurements: sample.measurements.map(measurement => {
            if (measurement.id === measurementId) {
              const newFieldId = Math.max(...measurement.fields.map(f => f.id), 0) + 1;
              return {
                ...measurement,
                fields: [...measurement.fields, {
                  id: newFieldId,
                  question: 'Neue Frage',
                  type: 'text'
                }]
              };
            }
            return measurement;
          })
        };
      }
      return sample;
    }));
  };
  const handleUpdateField = (sampleId: number, measurementId: number, fieldId: number, updates: Partial<Field>) => {
    setSamples(samples.map(sample => {
      if (sample.id === sampleId) {
        return {
          ...sample,
          measurements: sample.measurements.map(measurement => {
            if (measurement.id === measurementId) {
              return {
                ...measurement,
                fields: measurement.fields.map(field => field.id === fieldId ? {
                  ...field,
                  ...updates
                } : field)
              };
            }
            return measurement;
          })
        };
      }
      return sample;
    }));
  };
  const handleDeleteField = (sampleId: number, measurementId: number, fieldId: number) => {
    setSamples(samples.map(sample => {
      if (sample.id === sampleId) {
        return {
          ...sample,
          measurements: sample.measurements.map(measurement => {
            if (measurement.id === measurementId) {
              return {
                ...measurement,
                fields: measurement.fields.filter(field => field.id !== fieldId)
              };
            }
            return measurement;
          })
        };
      }
      return sample;
    }));
  };
  const handleDeleteSample = (sampleId: number) => {
    setSamples(samples.filter(sample => sample.id !== sampleId));
  };
  const handleDeleteMeasurement = (sampleId: number, measurementId: number) => {
    setSamples(samples.map(sample => {
      if (sample.id === sampleId) {
        return {
          ...sample,
          measurements: sample.measurements.filter(m => m.id !== measurementId)
        };
      }
      return sample;
    }));
  };
  const handleUpdateMeasurement = (sampleId: number, measurementId: number, updates: Partial<MeasurementConfig>) => {
    setSamples(samples.map(sample => {
      if (sample.id === sampleId) {
        return {
          ...sample,
          measurements: sample.measurements.map(measurement => measurement.id === measurementId ? {
            ...measurement,
            ...updates
          } : measurement)
        };
      }
      return sample;
    }));
  };
  const handleUpdateSample = (sampleId: number, updates: Partial<SampleConfig>) => {
    setSamples(samples.map(sample => sample.id === sampleId ? {
      ...sample,
      ...updates
    } : sample));
  };
  const handleAddMachine = () => {
    const newMachineId = (machines.length + 1).toString();
    setMachines([...machines, {
      id: newMachineId,
      name: newMachine.name || `Perlmühle ${newMachineId}`,
      type: newMachine.type,
      settings: {
        tempLowerLimit: newMachine.settings.tempLowerLimit,
        tempUpperLimit: newMachine.settings.tempUpperLimit,
        pearlWearLimit: newMachine.settings.pearlWearLimit
      },
      notifications: true
    }]);
    setIsModalOpen(false);
    setSelectedMachineType('');
    setNewMachine({
      name: '',
      type: '',
      settings: {
        tempLowerLimit: 45,
        tempUpperLimit: 60,
        pearlWearLimit: 10
      }
    });
  };
  const tabs = [{
    id: 'general',
    name: 'Allgemein',
    icon: <SettingsIcon size={16} data-id="element-789" />
  }, {
    id: 'samples',
    name: 'Proben',
    icon: <BeakerIcon size={16} data-id="element-790" />
  }, {
    id: 'machines',
    name: 'Maschinen',
    icon: <GaugeIcon size={16} data-id="element-791" />
  }, {
    id: 'tanks',
    name: 'Tankbestände',
    icon: <DatabaseIcon size={16} data-id="element-792" />
  }];
  const renderGeneralSettings = () => <div className="space-y-4" data-id="element-793">
      <div className="bg-white rounded-lg shadow-sm" data-id="element-794">
        <div className="border-b border-gray-200" data-id="element-795">
          <div className="flex items-center px-4 py-2" data-id="element-796">
            <ClockIcon className="text-[#3498dd] mr-2" size={16} data-id="element-797" />
            <h3 className="text-sm font-semibold text-gray-800" data-id="element-798">
              Automatisierte Datenerfassung
            </h3>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="p-4" data-id="element-799">
          <div className="grid gap-4" data-id="element-800">
            <div className="grid grid-cols-2 gap-4" data-id="element-801">
              <div className="bg-gray-50 p-3 rounded border border-gray-200" data-id="element-802">
                <div className="text-xs text-gray-600 mb-1" data-id="element-803">Intervall</div>
                <div className="flex items-center gap-2" data-id="element-804">
                  <input type="number" min="1" max="120" value={minutes} onChange={e => setMinutes(Number(e.target.value))} className="w-32 px-2 py-1 border rounded text-sm focus:outline-none focus:border-[#3498dd] bg-white" data-id="element-805" />
                  <span className="text-sm text-gray-700" data-id="element-806">Minuten</span>
                </div>
              </div>
              <div className="bg-gray-50 p-3 rounded border border-gray-200" data-id="element-807">
                <div className="text-xs text-gray-600 mb-1" data-id="element-808">Aktuelle Zeit</div>
                <div className="flex items-center gap-2" data-id="element-809">
                  <div className="text-sm text-gray-700" data-id="element-810">
                    {minutes * 60} Sekunden
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between pt-2" data-id="element-811">
              <div className="text-xs text-gray-500" data-id="element-812">
                Der Timer wird nach dem Speichern automatisch aktualisiert
              </div>
              <button type="submit" className="flex items-center gap-1 bg-[#3498dd] text-white px-3 py-1.5 rounded text-xs hover:bg-blue-600" data-id="element-813">
                <SaveIcon size={14} data-id="element-814" />
                Speichern
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>;
  const renderSampleSettings = () => <div className="space-y-6" data-id="element-815">
      <div className="bg-white rounded-lg shadow-sm" data-id="element-816">
        <div className="border-b border-gray-200" data-id="element-817">
          <div className="flex items-center justify-between px-4 py-2" data-id="element-818">
            <div className="flex items-center" data-id="element-819">
              <BeakerIcon className="text-[#3498dd] mr-2" size={16} data-id="element-820" />
              <h3 className="text-sm font-semibold text-gray-800" data-id="element-821">
                Proben Konfiguration
              </h3>
            </div>
            <button onClick={handleAddSample} className="inline-flex items-center gap-1.5 text-[#3498dd] hover:text-blue-600 text-sm bg-blue-50 px-3 py-1.5 rounded-lg transition-colors" data-id="element-822">
              <PlusCircleIcon size={16} data-id="element-823" />
              Neue Probe
            </button>
          </div>
        </div>
        <div className="p-4" data-id="element-824">
          <div className="grid grid-cols-1 gap-6" data-id="element-825">
            {/* Render all samples in the same way, not just the first one */}
            {samples.map(sample => <div key={sample.id} className="bg-gray-50 rounded-lg border border-gray-200 hover:border-[#3498dd] transition-colors flex flex-col max-h-full" data-id="element-826">
                {/* Proben-Header - sticky */}
                <div className="sticky top-0 bg-gray-50 p-4 border-b border-gray-200 rounded-t-lg z-10" data-id="element-827">
                  <div className="flex items-center justify-between" data-id="element-828">
                    <div className="flex items-center gap-2" data-id="element-829">
                      <h4 className="font-medium text-gray-800" data-id="element-830">
                        Probe {sample.id}
                      </h4>
                      <div className="text-xs text-gray-500" data-id="element-831">
                        ({sample.timestamps.length} Zeitpunkte)
                      </div>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-832">
                      <button onClick={() => handleDeleteSample(sample.id)} className="text-gray-400 hover:text-red-500 transition-colors p-1 rounded-lg hover:bg-red-50" data-id="element-833">
                        <TrashIcon size={16} data-id="element-834" />
                      </button>
                      <button onClick={() => setExpandedSample(expandedSample === sample.id ? null : sample.id)} className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100" data-id="element-835">
                        {expandedSample === sample.id ? <ChevronUpIcon size={16} data-id="element-836" /> : <ChevronDownIcon size={16} data-id="element-837" />}
                      </button>
                    </div>
                  </div>
                </div>
                {expandedSample === sample.id && <div className="p-4 space-y-6 overflow-y-auto flex-1" data-id="element-838">
                    {/* Basis-Informationen und Benachrichtigungen in 1x2 Grid */}
                    <div className="grid grid-cols-2 gap-6 mb-6" data-id="element-839">
                      <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-840">
                        <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-841">
                          Proben-Name
                        </label>
                        <input type="text" value={sample.title} onChange={e => handleUpdateSample(sample.id, {
                    title: e.target.value
                  })} className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3498dd]" placeholder="Name der Probe eingeben" data-id="element-842" />
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-843">
                        <label className="flex items-center space-x-2 cursor-pointer" data-id="element-844">
                          <input type="checkbox" checked={sample.notification} onChange={e => handleUpdateSample(sample.id, {
                      notification: e.target.checked
                    })} className="rounded text-[#3498dd] focus:ring-[#3498dd]" data-id="element-845" />
                          <div data-id="element-846">
                            <span className="text-sm text-gray-700" data-id="element-847">
                              Benachrichtigung aktivieren
                            </span>
                            <p className="text-xs text-gray-500 mt-0.5" data-id="element-848">
                              Erhalten Sie Erinnerungen für die Probenentnahme
                            </p>
                          </div>
                        </label>
                      </div>
                    </div>
                    {/* Zeitpunkte in voller Breite */}
                    <div className="bg-white p-4 rounded-lg border border-gray-200 mb-6" data-id="element-849">
                      <div className="flex items-center justify-between mb-4" data-id="element-850">
                        <div data-id="element-851">
                          <h5 className="text-xs font-medium text-gray-700" data-id="element-852">
                            Zeitpunkte
                          </h5>
                          <p className="text-xs text-gray-500 mt-0.5" data-id="element-853">
                            Definieren Sie die Messzeiten
                          </p>
                        </div>
                        {(sample.timingType === 'custom' || !sample.timingType) && <button onClick={() => handleAddTimestamp(sample.id)} className="inline-flex items-center gap-1.5 text-xs text-[#3498dd] hover:text-blue-600 bg-blue-50 px-2.5 py-1.5 rounded-lg transition-colors" data-id="element-854">
                            <PlusCircleIcon size={14} data-id="element-855" />
                            Zeitpunkt
                          </button>}
                      </div>
                      {/* Neue Checkbox-Auswahl für den Probenzeitpunkt - nebeneinander */}
                      <div className="mb-4 bg-gray-50 p-3 rounded-lg border border-gray-200" data-id="element-856">
                        <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-857">
                          Probenzeitpunkt
                        </label>
                        <div className="grid grid-cols-3 gap-3" data-id="element-858">
                          <label className="flex items-center space-x-2 cursor-pointer" data-id="element-859">
                            <input type="checkbox" checked={sample.timingType === 'custom' || !sample.timingType} onChange={e => handleUpdateSample(sample.id, {
                        timingType: e.target.checked ? 'custom' : undefined
                      })} className="rounded text-[#3498dd] focus:ring-[#3498dd]" data-id="element-860" />
                            <span className="text-sm text-gray-700" data-id="element-861">
                              Uhrzeit
                            </span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer" data-id="element-862">
                            <input type="checkbox" checked={sample.timingType === 'start' || sample.timingType === 'both'} onChange={e => {
                        const currentIsEnd = sample.timingType === 'end' || sample.timingType === 'both';
                        let newType;
                        if (e.target.checked && currentIsEnd) newType = 'both';else if (e.target.checked) newType = 'start';else if (currentIsEnd) newType = 'end';else newType = 'custom';
                        handleUpdateSample(sample.id, {
                          timingType: newType
                        });
                      }} className="rounded text-[#3498dd] focus:ring-[#3498dd]" data-id="element-863" />
                            <span className="text-sm text-gray-700" data-id="element-864">
                              Bei Produktionsstart
                            </span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer" data-id="element-865">
                            <input type="checkbox" checked={sample.timingType === 'end' || sample.timingType === 'both'} onChange={e => {
                        const currentIsStart = sample.timingType === 'start' || sample.timingType === 'both';
                        let newType;
                        if (e.target.checked && currentIsStart) newType = 'both';else if (e.target.checked) newType = 'end';else if (currentIsStart) newType = 'start';else newType = 'custom';
                        handleUpdateSample(sample.id, {
                          timingType: newType
                        });
                      }} className="rounded text-[#3498dd] focus:ring-[#3498dd]" data-id="element-866" />
                            <span className="text-sm text-gray-700" data-id="element-867">
                              Bei Produktionsende
                            </span>
                          </label>
                        </div>
                        {(sample.timingType === 'start' || sample.timingType === 'end' || sample.timingType === 'both') && <p className="text-xs text-gray-500 mt-2" data-id="element-868">
                            {sample.timingType === 'start' && 'Probe wird automatisch bei Produktionsstart durchgeführt.'}
                            {sample.timingType === 'end' && 'Probe wird automatisch bei Produktionsende durchgeführt.'}
                            {sample.timingType === 'both' && 'Probe wird automatisch bei Produktionsstart und -ende durchgeführt.'}
                          </p>}
                      </div>
                      {/* Zeige Zeitpunkte nur an, wenn benutzerdefiniert ausgewählt ist */}
                      {(sample.timingType === 'custom' || !sample.timingType) && <div className="space-y-2" data-id="element-869">
                          {sample.timestamps.map((timestamp, index) => <div key={index} className="flex items-center gap-2 bg-gray-50 p-2 rounded-lg" data-id="element-870">
                              <input type="time" value={timestamp} onChange={e => handleUpdateTimestamp(sample.id, index, e.target.value)} className="flex-1 px-3 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3498dd] bg-white" data-id="element-871" />
                              {sample.timestamps.length > 1 && <button onClick={() => handleDeleteTimestamp(sample.id, index)} className="text-gray-400 hover:text-red-500 p-1.5 rounded-lg hover:bg-red-50" data-id="element-872">
                                  <TrashIcon size={14} data-id="element-873" />
                                </button>}
                            </div>)}
                        </div>}
                    </div>
                    {/* Messungen */}
                    <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-874">
                      <div className="flex items-center justify-between mb-4" data-id="element-875">
                        <div data-id="element-876">
                          <h5 className="text-sm font-medium text-gray-700" data-id="element-877">
                            Messungen
                          </h5>
                          <p className="text-xs text-gray-500 mt-0.5" data-id="element-878">
                            Definieren Sie die Messparameter
                          </p>
                        </div>
                        <button onClick={() => handleAddMeasurement(sample.id)} className="inline-flex items-center gap-1.5 text-xs text-[#3498dd] hover:text-blue-600 bg-blue-50 px-2.5 py-1.5 rounded-lg transition-colors" data-id="element-879">
                          <PlusCircleIcon size={14} data-id="element-880" />
                          Messung
                        </button>
                      </div>
                      <div className="space-y-4" data-id="element-881">
                        {sample.measurements.map(measurement => <div key={measurement.id} className="bg-gray-50 rounded-lg border border-gray-200 p-4 relative group" data-id="element-882">
                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-883">
                              <button onClick={() => handleDeleteMeasurement(sample.id, measurement.id)} className="text-gray-400 hover:text-red-500 p-1.5 rounded-lg hover:bg-red-50" data-id="element-884">
                                <TrashIcon size={14} data-id="element-885" />
                              </button>
                            </div>
                            <div className="space-y-4" data-id="element-886">
                              {/* Messungs-Titel */}
                              <div data-id="element-887">
                                <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-888">
                                  Messungs-Bezeichnung
                                </label>
                                <input type="text" value={measurement.title} onChange={e => handleUpdateMeasurement(sample.id, measurement.id, {
                          title: e.target.value
                        })} className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3498dd] bg-white" placeholder="Bezeichnung eingeben" data-id="element-889" />
                              </div>
                              {/* Eingabefelder */}
                              <div data-id="element-890">
                                <div className="flex items-center justify-between mb-3" data-id="element-891">
                                  <div data-id="element-892">
                                    <h6 className="text-xs font-medium text-gray-700" data-id="element-893">
                                      Eingabefelder
                                    </h6>
                                    <p className="text-xs text-gray-500 mt-0.5" data-id="element-894">
                                      Definieren Sie die Messwerte
                                    </p>
                                  </div>
                                  <button onClick={() => handleAddField(sample.id, measurement.id)} className="inline-flex items-center gap-1.5 text-xs text-[#3498dd] hover:text-blue-600 bg-blue-50 px-2.5 py-1.5 rounded-lg transition-colors" data-id="element-895">
                                    <PlusCircleIcon size={14} data-id="element-896" />
                                    Feld
                                  </button>
                                </div>
                                <div className="space-y-3" data-id="element-897">
                                  {measurement.fields.map(field => <div key={field.id} className="flex items-start gap-2 bg-white p-3 rounded-lg border border-gray-200 group/field" data-id="element-898">
                                      <div className="flex-1 space-y-2" data-id="element-899">
                                        <input type="text" value={field.question} onChange={e => handleUpdateField(sample.id, measurement.id, field.id, {
                                question: e.target.value
                              })} placeholder="Frage eingeben..." className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3498dd]" data-id="element-900" />
                                        <select value={field.type} onChange={e => handleUpdateField(sample.id, measurement.id, field.id, {
                                type: e.target.value as 'text' | 'number' | 'boolean'
                              })} className="px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3498dd] bg-white" data-id="element-901">
                                          <option value="text" data-id="element-902">Text</option>
                                          <option value="number" data-id="element-903">Zahl</option>
                                          <option value="boolean" data-id="element-904">
                                            Ja/Nein
                                          </option>
                                        </select>
                                      </div>
                                      <button onClick={() => handleDeleteField(sample.id, measurement.id, field.id)} className="opacity-0 group-hover/field:opacity-100 transition-opacity text-gray-400 hover:text-red-500 p-1.5 rounded-lg hover:bg-red-50" data-id="element-905">
                                        <TrashIcon size={14} data-id="element-906" />
                                      </button>
                                    </div>)}
                                </div>
                              </div>
                              {/* Pflichtfeld-Checkbox */}
                              <div className="pt-2" data-id="element-907">
                                <label className="flex items-center space-x-2 cursor-pointer" data-id="element-908">
                                  <input type="checkbox" checked={measurement.required} onChange={e => handleUpdateMeasurement(sample.id, measurement.id, {
                            required: e.target.checked
                          })} className="rounded text-[#3498dd] focus:ring-[#3498dd]" data-id="element-909" />
                                  <span className="text-sm text-gray-600" data-id="element-910">
                                    Pflichtfeld
                                  </span>
                                </label>
                              </div>
                            </div>
                          </div>)}
                      </div>
                    </div>
                  </div>}
              </div>)}
          </div>
          {/* Neue Probe Button */}
          <button onClick={handleAddSample} className="w-full mt-6 bg-gray-50 p-4 rounded-lg border border-dashed border-gray-300 hover:border-[#3498dd] hover:bg-blue-50/50 transition-colors flex flex-col items-center justify-center gap-2 text-gray-500 hover:text-[#3498dd]" data-id="element-911">
            <PlusCircleIcon size={24} data-id="element-912" />
            <span className="text-sm" data-id="element-913">Neue Probe hinzufügen</span>
          </button>
          {/* Speichern Button */}
          <div className="flex justify-end mt-6" data-id="element-914">
            <button className="flex items-center gap-1.5 bg-[#3498dd] text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-600 transition-colors" data-id="element-915">
              <SaveIcon size={16} data-id="element-916" />
              Speichern
            </button>
          </div>
        </div>
      </div>
    </div>;
  const renderMachineSettings = () => {
    return <div className="space-y-6" data-id="element-917">
        <div className="bg-white rounded-lg shadow-sm" data-id="element-918">
          <div className="border-b border-gray-200" data-id="element-919">
            <div className="flex items-center justify-between px-4 py-2" data-id="element-920">
              <div className="flex items-center" data-id="element-921">
                <GaugeIcon className="text-[#3498dd] mr-2" size={16} data-id="element-922" />
                <h3 className="text-sm font-semibold text-gray-800" data-id="element-923">
                  Maschineneinstellungen
                </h3>
              </div>
              <button onClick={() => setIsModalOpen(true)} className="inline-flex items-center gap-1.5 text-[#3498dd] hover:text-blue-600 text-sm bg-blue-50 px-3 py-1.5 rounded-lg transition-colors" data-id="element-924">
                <PlusCircleIcon size={16} data-id="element-925" />
                Neue Maschine
              </button>
            </div>
          </div>
          <div className="p-4" data-id="element-926">
            <div className="grid grid-cols-1 gap-6" data-id="element-927">
              {machines.map(machine => <div key={machine.id} className="bg-gray-50 rounded-lg border border-gray-200 hover:border-[#3498dd] transition-colors" data-id="element-928">
                  <div className="p-4 border-b border-gray-200" data-id="element-929">
                    <div className="flex items-center justify-between" data-id="element-930">
                      <div className="flex items-center gap-2" data-id="element-931">
                        <h4 className="font-medium text-gray-800" data-id="element-932">
                          {machine.name}
                        </h4>
                      </div>
                    </div>
                  </div>
                  <div className="p-4" data-id="element-933">
                    <div className="grid grid-cols-2 gap-6" data-id="element-934">
                      <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-935">
                        <label className="flex items-center space-x-2 cursor-pointer" data-id="element-936">
                          <input type="checkbox" checked={machine.notifications} onChange={e => {
                        setMachines(machines.map(m => m.id === machine.id ? {
                          ...m,
                          notifications: e.target.checked
                        } : m));
                      }} className="rounded text-[#3498dd] focus:ring-[#3498dd]" data-id="element-937" />
                          <div data-id="element-938">
                            <span className="text-sm text-gray-700" data-id="element-939">
                              Benachrichtigung aktivieren
                            </span>
                            <p className="text-xs text-gray-500 mt-0.5" data-id="element-940">
                              Erhalten Sie Benachrichtigungen bei
                              Grenzwertüberschreitungen
                            </p>
                          </div>
                        </label>
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-941">
                        <h5 className="text-sm font-medium text-gray-700 mb-3" data-id="element-942">
                          Benachrichtigungseinstellungen
                        </h5>
                        <div className="space-y-4" data-id="element-943">
                          <div data-id="element-944">
                            <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-945">
                              Temperatur-Grenzwerte
                            </label>
                            <div className="grid grid-cols-2 gap-3" data-id="element-946">
                              <div data-id="element-947">
                                <label className="block text-xs text-gray-500 mb-1" data-id="element-948">
                                  Untergrenze (°C)
                                </label>
                                <input type="number" value={machine.settings.tempLowerLimit} onChange={e => {
                              setMachines(machines.map(m => m.id === machine.id ? {
                                ...m,
                                settings: {
                                  ...m.settings,
                                  tempLowerLimit: Number(e.target.value)
                                }
                              } : m));
                            }} className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3498dd]" data-id="element-949" />
                              </div>
                              <div data-id="element-950">
                                <label className="block text-xs text-gray-500 mb-1" data-id="element-951">
                                  Obergrenze (°C)
                                </label>
                                <input type="number" value={machine.settings.tempUpperLimit} onChange={e => {
                              setMachines(machines.map(m => m.id === machine.id ? {
                                ...m,
                                settings: {
                                  ...m.settings,
                                  tempUpperLimit: Number(e.target.value)
                                }
                              } : m));
                            }} className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3498dd]" data-id="element-952" />
                              </div>
                            </div>
                          </div>
                          <div data-id="element-953">
                            <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-954">
                              Perlen-Verschleiß
                            </label>
                            <div className="flex items-end gap-3" data-id="element-955">
                              <div className="flex-1" data-id="element-956">
                                <label className="block text-xs text-gray-500 mb-1" data-id="element-957">
                                  Benachrichtigung bei Verschleiß über
                                </label>
                                <input type="number" value={machine.settings.pearlWearLimit} onChange={e => {
                              setMachines(machines.map(m => m.id === machine.id ? {
                                ...m,
                                settings: {
                                  ...m.settings,
                                  pearlWearLimit: Number(e.target.value)
                                }
                              } : m));
                            }} className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3498dd]" data-id="element-958" />
                              </div>
                              <span className="text-sm text-gray-500 pb-2" data-id="element-959">
                                %
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>)}
              {/* Add Machine Button */}
              <button onClick={() => setIsModalOpen(true)} className="w-full mt-6 bg-gray-50 p-4 rounded-lg border border-dashed border-gray-300 hover:border-[#3498dd] hover:bg-blue-50/50 transition-colors flex flex-col items-center justify-center gap-2 text-gray-500 hover:text-[#3498dd]" data-id="element-960">
                <PlusCircleIcon size={24} data-id="element-961" />
                <span className="text-sm" data-id="element-962">Neue Maschine hinzufügen</span>
              </button>
              {/* Save Button */}
              <div className="flex justify-end mt-6" data-id="element-963">
                <button className="flex items-center gap-1.5 bg-[#3498dd] text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-600 transition-colors" data-id="element-964">
                  <SaveIcon size={16} data-id="element-965" />
                  Speichern
                </button>
              </div>
            </div>
          </div>
        </div>
        {isModalOpen && <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50" data-id="element-966">
            <div className="bg-white rounded-lg p-6 w-[800px] max-w-full m-4" data-id="element-967">
              <div className="flex justify-between items-center mb-6" data-id="element-968">
                <h4 className="text-lg font-semibold text-gray-800" data-id="element-969">
                  Neue Maschine hinzufügen
                </h4>
                <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600" data-id="element-970">
                  <XIcon size={20} data-id="element-971" />
                </button>
              </div>
              <div className="space-y-6" data-id="element-972">
                {!selectedMachineType ? <div className="grid grid-cols-2 gap-4" data-id="element-973">
                    {availableMachineTypes.map(machine => <button key={machine.id} onClick={() => {
                setSelectedMachineType(machine.id);
                setNewMachine(prev => ({
                  ...prev,
                  type: machine.id
                }));
              }} className="text-left p-4 border border-gray-200 rounded-lg hover:border-[#3498dd] hover:bg-blue-50/50 transition-colors" data-id="element-974">
                        <h5 className="font-medium text-gray-800 mb-1" data-id="element-975">
                          {machine.name}
                        </h5>
                        <p className="text-sm text-gray-500 mb-2" data-id="element-976">
                          {machine.description}
                        </p>
                        <div className="text-xs text-[#3498dd] bg-blue-50 inline-block px-2 py-1 rounded" data-id="element-977">
                          Kapazität: {machine.capacity}kg
                        </div>
                      </button>)}
                  </div> : <div className="space-y-4" data-id="element-978">
                    <div data-id="element-979">
                      <label className="block text-sm text-gray-700 mb-1" data-id="element-980">
                        Maschinenname
                      </label>
                      <input type="text" value={newMachine.name} onChange={e => setNewMachine({
                  ...newMachine,
                  name: e.target.value
                })} placeholder="z.B. Perlmühle 1" className="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#3498dd]" data-id="element-981" />
                    </div>
                    <div className="grid grid-cols-2 gap-4" data-id="element-982">
                      <div data-id="element-983">
                        <label className="block text-sm text-gray-700 mb-1" data-id="element-984">
                          Temperatur-Grenzwerte
                        </label>
                        <div className="grid grid-cols-2 gap-3" data-id="element-985">
                          <div data-id="element-986">
                            <label className="block text-xs text-gray-500 mb-1" data-id="element-987">
                              Untergrenze (°C)
                            </label>
                            <input type="number" value={newMachine.settings.tempLowerLimit} onChange={e => setNewMachine({
                        ...newMachine,
                        settings: {
                          ...newMachine.settings,
                          tempLowerLimit: Number(e.target.value)
                        }
                      })} className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3498dd]" data-id="element-988" />
                          </div>
                          <div data-id="element-989">
                            <label className="block text-xs text-gray-500 mb-1" data-id="element-990">
                              Obergrenze (°C)
                            </label>
                            <input type="number" value={newMachine.settings.tempUpperLimit} onChange={e => setNewMachine({
                        ...newMachine,
                        settings: {
                          ...newMachine.settings,
                          tempUpperLimit: Number(e.target.value)
                        }
                      })} className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3498dd]" data-id="element-991" />
                          </div>
                        </div>
                      </div>
                      <div data-id="element-992">
                        <label className="block text-sm text-gray-700 mb-1" data-id="element-993">
                          Perlen-Verschleiß
                        </label>
                        <div className="flex items-end gap-3" data-id="element-994">
                          <div className="flex-1" data-id="element-995">
                            <label className="block text-xs text-gray-500 mb-1" data-id="element-996">
                              Benachrichtigung bei Verschleiß über
                            </label>
                            <input type="number" value={newMachine.settings.pearlWearLimit} onChange={e => setNewMachine({
                        ...newMachine,
                        settings: {
                          ...newMachine.settings,
                          pearlWearLimit: Number(e.target.value)
                        }
                      })} className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3498dd]" data-id="element-997" />
                          </div>
                          <span className="text-sm text-gray-500 pb-2" data-id="element-998">%</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-4" data-id="element-999">
                      <button onClick={() => {
                  setSelectedMachineType('');
                  setNewMachine({
                    name: '',
                    type: '',
                    settings: {
                      tempLowerLimit: 45,
                      tempUpperLimit: 60,
                      pearlWearLimit: 10
                    }
                  });
                }} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded" data-id="element-1000">
                        Zurück
                      </button>
                      <button onClick={handleAddMachine} className="px-4 py-2 text-sm text-white bg-[#3498dd] hover:bg-blue-600 rounded flex items-center gap-1.5" data-id="element-1001">
                        <SaveIcon size={16} data-id="element-1002" />
                        Speichern
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </div>}
      </div>;
  };
  const renderTankSettings = () => <div className="space-y-4" data-id="element-1003">
      <div className="bg-white rounded-lg shadow-sm" data-id="element-1004">
        <div className="border-b border-gray-200" data-id="element-1005">
          <div className="flex items-center px-4 py-2" data-id="element-1006">
            <DatabaseIcon className="text-[#3498dd] mr-2" size={16} data-id="element-1007" />
            <h3 className="text-sm font-semibold text-gray-800" data-id="element-1008">
              Tankbestände Einstellungen
            </h3>
          </div>
        </div>
        <div className="p-8 text-center text-gray-500" data-id="element-1009">
          Tankbestände werden in Zukunft implementiert.
        </div>
      </div>
    </div>;
  return <div className="space-y-4" data-id="element-1010">
      <div className="bg-white rounded-lg shadow-sm p-3" data-id="element-1011">
        <h2 className="text-base font-bold text-gray-800" data-id="element-1012">Einstellungen</h2>
      </div>
      <div className="bg-white rounded-lg shadow-sm" data-id="element-1013">
        <div className="border-b border-gray-200" data-id="element-1014">
          <div className="flex" data-id="element-1015">
            {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id as SettingsTab)} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 ${activeTab === tab.id ? 'border-[#3498dd] text-[#3498dd]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-1016">
                {tab.icon}
                {tab.name}
              </button>)}
          </div>
        </div>
        <div className="p-4" data-id="element-1017">
          {activeTab === 'general' && renderGeneralSettings()}
          {activeTab === 'samples' && renderSampleSettings()}
          {activeTab === 'machines' && renderMachineSettings()}
          {activeTab === 'tanks' && renderTankSettings()}
        </div>
      </div>
    </div>;
}